import { useState } from 'react'
import { useSSHCredentials, useCreateSSHCredential, useDeleteSSHCredential, useUpdateSSHCredential } from '@/api/devices'
import { RequirePermission } from '@/components/RequirePermission'
import { useRoles } from '@/hooks/usePermission'
import type { UpdateSSHCredentialRequest } from '@/types'
import type { SSHCredentialSummary } from '@/api/devices'

interface Props {
  tenantId: string | null
}

const inputStyle: React.CSSProperties = {
  background: 'var(--color-surface-2)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text)',
  padding: '8px 12px',
  borderRadius: 'var(--radius-sm)',
  fontSize: 13,
  width: '100%',
}

// ── Edit Credential Modal ────────────────────────────────────────────────────

interface EditCredModalProps {
  cred: SSHCredentialSummary
  tenantId: string
  onClose: () => void
}

function EditCredentialModal({ cred, tenantId, onClose }: EditCredModalProps) {
  const update = useUpdateSSHCredential(tenantId)
  const [name, setName] = useState(cred.name)
  const [username, setUsername] = useState(cred.username)
  const [description, setDescription] = useState(cred.description ?? '')
  const [newSecret, setNewSecret] = useState('')
  const [authType, setAuthType] = useState<'password' | 'private_key'>(cred.auth_type)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const req: UpdateSSHCredentialRequest = {}
    if (name !== cred.name) req.name = name
    if (username !== cred.username) req.username = username
    if (description !== (cred.description ?? '')) req.description = description || undefined
    if (newSecret.trim()) {
      if (authType === 'password') req.password = newSecret
      else req.private_key = newSecret
    }
    await update.mutateAsync({ credId: cred.id, req })
    onClose()
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: 24, width: 480, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
        <h3 style={{ margin: '0 0 18px', fontSize: 15, fontWeight: 600 }}>Edit credential — {cred.name}</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Name</label>
              <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} required />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>SSH username</label>
              <input style={inputStyle} value={username} onChange={e => setUsername(e.target.value)} required />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Description</label>
              <input style={inputStyle} value={description} onChange={e => setDescription(e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                New secret (leave empty to keep current)
              </label>
              <select style={{ ...inputStyle, cursor: 'pointer', marginBottom: 6 }} value={authType} onChange={e => setAuthType(e.target.value as 'password' | 'private_key')}>
                <option value="password">Password</option>
                <option value="private_key">Private key</option>
              </select>
              <textarea
                style={{ ...inputStyle, fontFamily: 'var(--font-mono)', fontSize: 12, resize: 'vertical', minHeight: 72 }}
                placeholder={authType === 'password' ? 'New password (optional)' : 'PEM private key (optional)'}
                value={newSecret}
                onChange={e => setNewSecret(e.target.value)}
              />
            </div>
          </div>
          {update.isError && (
            <p style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>
              {(update.error as Error).message}
            </p>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export function Credentials({ tenantId }: Props) {
  const { data, isLoading } = useSSHCredentials(tenantId)
  const createCred = useCreateSSHCredential(tenantId ?? '')
  const deleteCred = useDeleteSSHCredential(tenantId ?? '')
  const roles = useRoles()
  const isPlatformAdmin = roles.includes('platform-admin')

  const [showCreate, setShowCreate] = useState(false)
  const [editCred, setEditCred] = useState<SSHCredentialSummary | null>(null)
  const [name, setName] = useState('')
  const [username, setUsername] = useState('')
  const [authType, setAuthType] = useState<'password' | 'private_key'>('password')
  const [secret, setSecret] = useState('')
  const [description, setDescription] = useState('')

  if (!tenantId) {
    return <div className="empty-state">Select a tenant to manage credentials.</div>
  }

  function resetForm() {
    setName('')
    setUsername('')
    setSecret('')
    setDescription('')
    setAuthType('password')
    setShowCreate(false)
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    await createCred.mutateAsync({
      name: name.trim(),
      username: username.trim(),
      password: authType === 'password' ? secret : undefined,
      private_key: authType === 'private_key' ? secret : undefined,
      description: description.trim() || undefined,
    })
    resetForm()
  }

  const creds = data?.data ?? []

  return (
    <div>
      {editCred && tenantId && (
        <EditCredentialModal
          cred={editCred}
          tenantId={tenantId}
          onClose={() => setEditCred(null)}
        />
      )}
      <div className="page-header">
        <div>
          <h1 className="page-title">SSH Credentials</h1>
          <p className="page-subtitle">
            {data?.total ?? 0} total — used during device bootstrap
          </p>
        </div>
        <RequirePermission perm="devices:write">
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            + New credential
          </button>
        </RequirePermission>
      </div>

      {showCreate && (
        <form
          onSubmit={handleCreate}
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            padding: 20,
            marginBottom: 24,
            maxWidth: 520,
          }}
        >
          <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600 }}>New SSH Credential</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                Name <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <input
                style={inputStyle}
                placeholder="e.g. modberry-root"
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                SSH username <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <input
                style={inputStyle}
                placeholder="e.g. root"
                value={username}
                onChange={e => setUsername(e.target.value)}
                required
              />
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                Authentication type
              </label>
              <select
                style={{ ...inputStyle, cursor: 'pointer' }}
                value={authType}
                onChange={e => setAuthType(e.target.value as 'password' | 'private_key')}
              >
                <option value="password">Password</option>
                <option value="private_key">Private Key (PEM)</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                {authType === 'password' ? 'Password' : 'Private key'} <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              {authType === 'password' ? (
                <input
                  style={inputStyle}
                  type="password"
                  placeholder="Password"
                  value={secret}
                  onChange={e => setSecret(e.target.value)}
                  required
                />
              ) : (
                <textarea
                  style={{ ...inputStyle, fontFamily: 'var(--font-mono)', fontSize: 12, resize: 'vertical' }}
                  placeholder="-----BEGIN RSA PRIVATE KEY-----..."
                  rows={6}
                  value={secret}
                  onChange={e => setSecret(e.target.value)}
                  required
                />
              )}
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                Description
              </label>
              <input
                style={inputStyle}
                placeholder="Optional description"
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
              <button type="submit" className="btn btn-primary" disabled={createCred.isPending}>
                {createCred.isPending ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={resetForm}>
                Cancel
              </button>
            </div>
            {createCred.isError && (
              <div style={{ color: 'var(--color-danger)', fontSize: 12 }}>
                Save failed. Check the console for details.
              </div>
            )}
          </div>
        </form>
      )}

      {isLoading && <div className="empty-state">Loading…</div>}

      {!isLoading && creds.length === 0 && (
        <div className="empty-state">
          No SSH credentials yet. Create one to enable device bootstrap.
        </div>
      )}

      {creds.length > 0 && (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>SSH username</th>
                <th>Auth type</th>
                <th>Status</th>
                {isPlatformAdmin && <th>AWX credential ID</th>}
                <th>Description</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {creds.map(c => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 500 }}>{c.name}</td>
                  <td><code style={{ fontSize: 12 }}>{c.username}</code></td>
                  <td>{c.auth_type === 'password' ? 'Password' : 'Private key'}</td>
                  <td>
                    {c.awx_credential_id
                      ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 3, background: 'rgba(34,197,94,0.12)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.3)' }}>● Registered</span>
                      : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 3, background: 'rgba(234,179,8,0.12)', color: '#facc15', border: '1px solid rgba(234,179,8,0.3)' }}>⟳ Pending</span>
                    }
                  </td>
                  {isPlatformAdmin && (
                    <td style={{ fontSize: 12, fontFamily: 'var(--font-mono)' }}>
                      {c.awx_credential_id ?? (
                        <span style={{ color: 'var(--color-text-muted)' }}>pending</span>
                      )}
                    </td>
                  )}
                  <td style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>
                    {c.description ?? '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <RequirePermission perm="devices:write">
                        <button
                          className="btn btn-ghost"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => setEditCred(c)}
                        >
                          Edit
                        </button>
                      </RequirePermission>
                      <RequirePermission perm="devices:write">
                        <button
                          className="btn btn-danger"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => {
                            if (window.confirm(`Delete credential "${c.name}"?`)) {
                              deleteCred.mutate(c.id)
                            }
                          }}
                        >
                          Delete
                        </button>
                      </RequirePermission>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
